import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import useAPI from 'hooks/useAPI';

export const SUPPORT_EMAIL = 'support@valital.com';

export function getHelpLegalUrls(language?: string) {
  const isFr = language?.includes('fr');
  return {
    termsUrl: isFr
      ? 'https://www.valital.com/fr/conditions-dutilisation'
      : 'https://www.valital.com/terms-of-use',
    privacyUrl: isFr
      ? 'https://www.valital.com/fr/declaration-de-confidentialite'
      : 'https://www.valital.com/privacy-statement',
  };
}

type ContactUsDialogProps = {
  open: boolean;
  onClose: () => void;
};

const ContactUsDialog: React.FC<ContactUsDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const API = useAPI();
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setMessage('');
    setSending(false);
    setSent(false);
    setError(null);
    onClose();
  };

  const handleSend = async () => {
    const trimmed = message.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setError(null);
    try {
      await API.post('/support/contact', { message: trimmed });
      setSent(true);
      setMessage('');
    } catch {
      setError(String(t('sentences:help_menu.contact_error')));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="contact-us-dialog-title"
    >
      <DialogTitle
        id="contact-us-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <ContactSupportOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          {String(t('sentences:help_menu.contact_us'))}
        </Box>
        <IconButton
          aria-label={String(t('sentences:help_menu.close'))}
          onClick={handleClose}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {sent ? (
          <Typography variant="body1" sx={{ py: 1 }}>
            {String(t('sentences:help_menu.contact_success'))}
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {String(t('sentences:help_menu.contact_hint'))}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={6}
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder={String(t('sentences:help_menu.contact_placeholder'))}
              disabled={sending}
              error={Boolean(error)}
              helperText={error || undefined}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Box sx={{ flex: 1 }} />
        {sent ? (
          <Button
            variant="contained"
            color="primary"
            onClick={handleClose}
            sx={{ textTransform: 'none', borderRadius: 999, px: 2.5 }}
          >
            {String(t('sentences:help_menu.close'))}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={() => void handleSend()}
            disabled={!message.trim() || sending}
            sx={{ textTransform: 'none', borderRadius: 999, px: 2.5 }}
          >
            {sending
              ? String(t('sentences:help_menu.sending'))
              : String(t('sentences:help_menu.send'))}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ContactUsDialog;
